import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

function BackupExport() {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState({});

  const handleExport = async (type, format) => {
    const key = `${type}-${format}`;
    setExporting(prev => ({ ...prev, [key]: true }));

    try {
      const response = await api.get(`/admin/export/${type}?format=${format}`, {
        responseType: 'blob'
      });

      // Create download link
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${type}_export_${dateStr}.${format}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleFullBackup = async () => {
    setExporting(prev => ({ ...prev, 'full-backup': true }));

    try {
      const response = await api.get('/admin/export/full-backup', {
        responseType: 'blob'
      });

      // Create download link
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `deedstack_full_backup_${dateStr}.json`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Full backup failed:', error);
      alert('Full backup failed. Please try again.');
    } finally {
      setExporting(prev => ({ ...prev, 'full-backup': false }));
    }
  };

  const exportOptions = [
    {
      id: 'properties',
      icon: '🏠',
      title: 'Properties Export',
      description: 'Export all property listings with details, pricing, and status'
    },
    {
      id: 'customers',
      icon: '👥',
      title: 'Customers Export',
      description: 'Export customer accounts with contact information'
    },
    {
      id: 'loans',
      icon: '💰',
      title: 'Loans Export',
      description: 'Export all loans with balances, terms, and payment schedules'
    },
    {
      id: 'payments',
      icon: '💳',
      title: 'Payments Export',
      description: 'Export complete payment history'
    }
  ];

  return (
    <div className="admin-dashboard-container">
      {/* Header with Back Button */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin/settings-hub')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Backup & Export</h1>
        <p className="admin-hub-subtitle">Download your data for backup or migration</p>
      </div>

      {/* Export Options Grid */}
      <div className="backup-export-grid">
        {exportOptions.map((option) => (
          <div key={option.id} className="backup-export-card">
            <div className="backup-export-icon">{option.icon}</div>
            <h3 className="backup-export-title">{option.title}</h3>
            <p className="backup-export-description">{option.description}</p>
            <div className="backup-export-buttons">
              <button
                className="btn btn-secondary backup-export-btn"
                onClick={() => handleExport(option.id, 'csv')}
                disabled={exporting[`${option.id}-csv`]}
              >
                {exporting[`${option.id}-csv`] ? 'Exporting...' : 'Download CSV'}
              </button>
              <button
                className="btn btn-secondary backup-export-btn"
                onClick={() => handleExport(option.id, 'json')}
                disabled={exporting[`${option.id}-json`]}
              >
                {exporting[`${option.id}-json`] ? 'Exporting...' : 'Download JSON'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Full Backup Section */}
      <div className="backup-full-section">
        <div className="backup-full-card">
          <div className="backup-full-content">
            <div className="backup-full-icon">📦</div>
            <div className="backup-full-info">
              <h3 className="backup-full-title">Complete Backup</h3>
              <p className="backup-full-description">
                Download everything in a single JSON file. Includes properties, customers, loans, and payments.
              </p>
            </div>
          </div>
          <button
            className="btn btn-primary backup-full-btn"
            onClick={handleFullBackup}
            disabled={exporting['full-backup']}
          >
            {exporting['full-backup'] ? 'Preparing Backup...' : 'Download Full Backup'}
          </button>
        </div>
      </div>

      {/* Info Note */}
      <div className="backup-info-note">
        <span className="backup-info-icon">ℹ️</span>
        <p>
          Exports include all data visible to your account. For security, customer passwords are never included in exports.
          Backups are recommended before making major changes or migrating to a new system.
        </p>
      </div>
    </div>
  );
}

export default BackupExport;
